import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { TextField } from 'components/Field/TextField';
import { ICONS } from 'components/Icon';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageFlatList } from 'components/Page';
import { useEsc } from 'hooks/useEsc';
import { theme } from 'lib/theme';
import { without } from 'lodash';
import React from 'react';
import { ListRenderItem } from '@shopify/flash-list';
import { useDispatch } from 'react-redux';
import {
  eventSelectScreenCancel,
  eventSelectScreenClose,
  eventSelectScreenSelection,
  NAVIGATE_MODAL_BACK,
  SET_INCOMING_VALUE,
  eventSelectScreenAdded,
} from 'store/actions';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { arrayUnique, getSearchTermsFromSearchValue } from 'lib/riskbase';
import { pluralise } from 'lib/pluralise';
import { ListSelectAllCheckbox } from 'components/ListSelectAllCheckbox';
import { Txt } from 'components/Txt';
import { View } from 'react-native';

type SelectOptionInternal = {
  groupName: string;
  internalValue: number | string | undefined;
  name: string;
  value?: string;
  labelAlignTop?: boolean;
  key: string;
  isNewGroup: boolean;
  radioValue?: boolean;
  checkedValue?: boolean;
  isFirst: boolean;
  splitterAbove: boolean;
  icon?: ICONS;
  iconColor?: string;
  showQuickChoose?: boolean;
};

const RenderItem = React.memo(
  (
    option: SelectOptionInternal & {
      onPress: (value: number | string | undefined) => void;
      onPressAdd: ((value: number | string | undefined) => void) | undefined;
      onPressChoose: ((value: number | string | undefined) => void) | undefined;
    },
  ) => (
    <>
      {option.splitterAbove && (
        <SeparatorHorizontal
          style={{
            marginTop: theme.paddingHorizontal - theme.paddingHorizontalHalf,
            marginBottom: theme.paddingHorizontal,
          }}
        />
      )}
      <Card
        marginTop={!option.isFirst && option.isNewGroup}
        label={option.isNewGroup ? option.groupName : undefined}
        style={{ marginBottom: theme.paddingHorizontalHalf }}
      >
        <CardRow
          key={option.internalValue || -1}
          label={option.name}
          value={option.value}
          labelAlignTop={option.labelAlignTop}
          labelNumberOfLines={0}
          radioValue={option.radioValue}
          checkedValue={option.checkedValue}
          onPress={() => option.onPress(option.internalValue)}
          onAction={
            option.onPressChoose
              ? () => option.onPressChoose?.(option.internalValue)
              : option.onPressAdd
                ? () => option.onPressAdd?.(option.internalValue)
                : undefined
          }
          actionIcon={
            option.onPressChoose ? ICONS.ArrowRightLight : ICONS.PlusLight
          }
          icon={option.icon}
          iconColor={option.iconColor}
        />
      </Card>
    </>
  ),
);

export const SelectScreen: React.FC<ModalScreenProps<ModalScreens.SELECT>> = ({
  route,
}) => {
  const dispatch = useDispatch();

  const {
    fieldName,
    incomingValueKey,
    multiSelect,
    options,
    returnType = 'array',
    text,
    title,
    value,
    searchable,
    enableSelectAll,
    requireValue,
    optionNoun,
    eventKey,
    showQuickChoose,
    eventKeyAdded,
    closeOnDone = true,
    hideRadioButton,
    showDoneButtonForSingleSelect = true,
  } = route.params;

  const initValue = React.useRef(
    !value ? [] : Array.isArray(value) ? value : [value],
  );
  const [internalValue, setInternalValue] = React.useState<(string | number)[]>(
    initValue.current,
  );
  const [searchValue, setSearchValue] = React.useState('');

  const handleSearch = (newSearchValue: string) =>
    setSearchValue(newSearchValue);

  const searchValues = getSearchTermsFromSearchValue(searchValue);

  const {
    options: nonFlatOptions,
    filteredOutButSelected,
    includedIds,
    includedSelectedIds,
    includedNotSelectedIds,
    excludedSelectedIds,
  } = options.reduce<{
    isFirst: boolean;
    splitterBeforeNext: boolean;
    options: SelectOptionInternal[][];
    groupKeys: Record<string, number>;
    filteredOutButSelected: SelectOptionInternal[];
    includedIds: (string | number)[];
    includedSelectedIds: (string | number)[];
    includedNotSelectedIds: (string | number)[];
    excludedSelectedIds: (string | number)[];
  }>(
    (acc, option) => {
      if (option === '-') {
        if (!acc.isFirst) {
          acc.splitterBeforeNext = true;
        }
        return acc;
      }
      const selected = option.internalValue
        ? internalValue.includes(option.internalValue)
        : !internalValue.length;
      const details = option.name.toLowerCase();
      if (
        !searchValues.length ||
        searchValues.every((searchVal) => details.includes(searchVal))
      ) {
        const groupName = option.groupName || '';
        let isNewGroup = false;
        if (acc.groupKeys[groupName] === undefined) {
          isNewGroup = true; // acc.options.length > 0;
          acc.groupKeys[groupName] = acc.options.length;
          acc.options[acc.groupKeys[groupName]!] = [];
        }
        acc.options[acc.groupKeys[groupName]!]!.push({
          name: option.name,
          value: option.value,
          labelAlignTop: option.labelAlignTop,
          internalValue: option.internalValue,
          key: `${option.internalValue}-${option.name}`,
          radioValue: !multiSelect && !hideRadioButton ? selected : undefined,
          checkedValue: multiSelect ? selected : undefined,
          groupName,
          isNewGroup,
          splitterAbove: acc.splitterBeforeNext,
          isFirst: acc.isFirst,
          icon: option.icon,
          iconColor: option.iconColor,
        });
        if (option.internalValue) {
          acc.includedIds.push(option.internalValue);
          if (selected) {
            acc.includedSelectedIds.push(option.internalValue);
          } else {
            acc.includedNotSelectedIds.push(option.internalValue);
          }
        }
        acc.isFirst = false;
        acc.splitterBeforeNext = false;
      } else if (selected && multiSelect) {
        acc.filteredOutButSelected.push({
          name: option.name,
          value: option.value,
          labelAlignTop: option.labelAlignTop,
          internalValue: option.internalValue,
          key: `${option.internalValue}-${option.name}`,
          radioValue: false,
          checkedValue: true,
          groupName: '',
          isNewGroup: false,
          isFirst: false,
          splitterAbove: false,
          icon: option.icon,
          iconColor: option.iconColor,
        });
        if (option.internalValue) {
          acc.excludedSelectedIds.push(option.internalValue);
        }
        acc.splitterBeforeNext = false;
      }
      return acc;
    },
    {
      isFirst: true,
      splitterBeforeNext: false,
      options: [],
      groupKeys: {},
      filteredOutButSelected: [],
      includedIds: [],
      includedSelectedIds: [],
      includedNotSelectedIds: [],
      excludedSelectedIds: [],
    },
  );

  const flatOptions = nonFlatOptions.flat();

  const handleSave = React.useCallback(
    (newValue: (string | number)[]) => {
      if (closeOnDone) {
        dispatch(NAVIGATE_MODAL_BACK([ModalScreens.SELECT]));
      }
      // if (!arrayHasSameMembers(newValue, initValue.current)) {
      if (fieldName && incomingValueKey) {
        dispatch(
          SET_INCOMING_VALUE({
            key: incomingValueKey,
            value: {
              [fieldName]: newValue.length
                ? returnType === 'string'
                  ? newValue[0]
                  : returnType === 'number'
                    ? parseInt(`${newValue[0]}`, 10)
                    : newValue
                : returnType === 'string'
                  ? ''
                  : returnType === 'number'
                    ? 0
                    : [],
            },
          }),
        );
      }
      if (eventKey) {
        dispatch(
          eventSelectScreenSelection({
            key: eventKey,
            numberValues: newValue.filter(
              (v) => typeof v === 'number',
            ) as number[],
            stringValues: newValue.filter(
              (v) => typeof v === 'string',
            ) as string[],
          }),
        );
      }
      // }
    },
    [closeOnDone, dispatch, eventKey, fieldName, incomingValueKey, returnType],
  );

  const handleSelect = React.useCallback(
    (newValue: string | number | undefined) =>
      setInternalValue((curInternalValue) => {
        const newInternalValue = !newValue
          ? []
          : multiSelect
            ? curInternalValue.includes(newValue)
              ? without(curInternalValue, newValue)
              : [...curInternalValue, newValue]
            : [newValue];
        if (!multiSelect) {
          handleSave(newInternalValue);
        }
        return newInternalValue;
      }),
    [handleSave, multiSelect],
  );

  const handlePressChoose = React.useCallback(
    (newValue: string | number | undefined) => {
      if (eventKey && newValue) {
        if (closeOnDone) {
          dispatch(NAVIGATE_MODAL_BACK([ModalScreens.SELECT]));
        }
        dispatch(
          eventSelectScreenSelection({
            key: eventKey,
            numberValues: typeof newValue === 'number' ? [newValue] : [],
            stringValues: typeof newValue === 'string' ? [newValue] : [],
          }),
        );
      }
    },
    [closeOnDone, dispatch, eventKey],
  );

  const handlePressAdd = React.useCallback(
    (newValue: string | number | undefined) => {
      if (newValue && eventKeyAdded) {
        dispatch(
          eventSelectScreenAdded({
            key: eventKeyAdded,
            numberValue: typeof newValue === 'number' ? newValue : undefined,
            stringValue: typeof newValue === 'string' ? newValue : undefined,
          }),
        );
        dispatch(NAVIGATE_MODAL_BACK([ModalScreens.SELECT]));
      }
    },
    [dispatch, eventKeyAdded],
  );

  const handleClose = () => {
    dispatch(NAVIGATE_MODAL_BACK([ModalScreens.SELECT]));
    if (eventKey) {
      dispatch(
        eventSelectScreenCancel({
          key: eventKey,
        }),
      );
    }
  };

  useEsc(handleClose);

  const renderItem: ListRenderItem<SelectOptionInternal> = React.useCallback(
    ({ item }) => {
      return (
        <RenderItem
          name={item.name}
          value={item.value}
          labelAlignTop={item.labelAlignTop}
          key={item.key}
          internalValue={item.internalValue}
          isNewGroup={item.isNewGroup}
          groupName={item.groupName}
          radioValue={item.radioValue}
          checkedValue={item.checkedValue}
          onPress={handleSelect}
          onPressAdd={eventKeyAdded ? handlePressAdd : undefined}
          onPressChoose={
            eventKey && showQuickChoose ? handlePressChoose : undefined
          }
          isFirst={item.isFirst}
          splitterAbove={item.splitterAbove}
          icon={item.icon}
          iconColor={item.iconColor}
        />
      );
    },
    [
      handleSelect,
      eventKeyAdded,
      handlePressAdd,
      eventKey,
      showQuickChoose,
      handlePressChoose,
    ],
  );

  React.useEffect(() => {
    return () => {
      if (eventKey) {
        dispatch(
          eventSelectScreenClose({
            key: eventKey,
          }),
        );
      }
    };
  }, [dispatch, eventKey]);

  return (
    <PageFlatList
      header={
        <>
          <NavigationHeader
            onPressCancelButton={handleClose}
            onPressSaveButton={
              !showDoneButtonForSingleSelect && !multiSelect
                ? undefined
                : requireValue && !internalValue.length
                  ? null
                  : () => handleSave(internalValue)
            }
            saveButtonText="Done"
            cancelButtonText={multiSelect ? 'Cancel' : 'Close'}
            heading={
              (title || 'Select') + (searchValues.length ? ' (Filtered)' : '')
            }
            subheading={
              multiSelect
                ? pluralise(
                    internalValue.length,
                    '1 Selected',
                    '{x} Selected',
                    '',
                  )
                : ''
            }
          />
          {((multiSelect && enableSelectAll) || searchable) && (
            <NavigationHeader
              contentStyle={{
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'row',
                paddingBottom: theme.paddingHorizontal,
              }}
              marginLeft={0}
              marginRight={0}
            >
              {multiSelect && enableSelectAll && (
                <ListSelectAllCheckbox
                  onSelectAll={() =>
                    setInternalValue(
                      arrayUnique([...excludedSelectedIds, ...includedIds]),
                    )
                  }
                  onSelectNone={() => setInternalValue(excludedSelectedIds)}
                  selectionState={
                    includedSelectedIds.length
                      ? includedNotSelectedIds.length
                        ? 'some'
                        : 'all'
                      : 'none'
                  }
                />
              )}
              <TextField
                iconLeft={ICONS.MagnifyingGlassLight}
                onChange={handleSearch}
                placeholder={`Search ${optionNoun || title}`}
                clearable
                onChangeDebounceMs={300}
                containerStyle={{
                  backgroundColor: '#ffffff',
                  borderRadius: 4,
                  flex: 1,
                  height: 40,
                  overflow: 'hidden',
                }}
                onEsc={handleClose}
                autoFocusWeb
                autoFocusDelay
                autoCorrect={false}
                autoComplete="off"
                autoCapitalize="none"
              />
            </NavigationHeader>
          )}
        </>
      }
      contentPaddingTop={text ? 0 : theme.paddingPageVertical}
      listConfig={{
        ListHeaderComponent:
          text || !flatOptions.length ? (
            <>
              {!!text && (
                <View
                  style={{
                    backgroundColor: theme.colors.orange10,
                    paddingVertical: theme.paddingHorizontal,
                    paddingHorizontal: theme.paddingHorizontal * 2,
                    marginBottom: theme.paddingHorizontal,
                  }}
                >
                  <Txt size="small">{text}</Txt>
                </View>
              )}
              {!flatOptions.length && (
                <Card marginBottomQuarter>
                  <CardRow
                    placeholder={`No ${optionNoun || 'Options'} Found`}
                  />
                </Card>
              )}
            </>
          ) : null,
        ListFooterComponent: filteredOutButSelected.length ? (
          <Card
            label="Selected but hidden by filter"
            style={{
              marginBottom: theme.paddingHorizontalHalf,
              marginTop: theme.paddingVertical - theme.paddingHorizontalHalf,
            }}
          >
            {filteredOutButSelected.map((option) => (
              <CardRow
                key={option.key}
                label={option.name}
                checkedValue={option.checkedValue}
                radioValue={option.radioValue}
                onPress={() => handleSelect(option.internalValue)}
              />
            ))}
          </Card>
        ) : null,
        keyExtractor: (item) => item.key,
        renderItem,
        data: flatOptions,
      }}
    />
  );
};
